import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Award, MapPin, Store as StoreIcon, Package } from 'lucide-react';
import { reviewsAPI } from '../lib/api';
import { RatingBadge } from './RatingBadge';
import { optimizeCloudinaryUrl } from '../lib/cloudinary';

const formatPrice = (v) =>
  typeof v === 'number' ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v) : null;

/**
 * Seção "Mais bem avaliados" — usa ranking bayesiano do backend
 * (min_reviews evita produto com 1 review de 5★ ultrapassar produtos com muitas reviews).
 */
export const TopRatedSection = ({ limit = 8, minReviews = 3 }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    reviewsAPI
      .topRated(limit, minReviews)
      .then(({ data }) => { if (alive) setItems(data.items || []); })
      .catch(() => { if (alive) setItems([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [limit, minReviews]);

  if (loading || items.length === 0) return null;

  return (
    <section className="px-4 pt-6" data-testid="top-rated-section">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 text-white shadow-sm">
            <Award className="w-4 h-4" />
          </span>
          Mais bem avaliados
        </h2>
        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Top {items.length}</span>
      </div>

      <div className="flex gap-3 overflow-x-auto snap-x snap-mandatory hide-scrollbar pb-2 -mx-4 px-4">
        {items.map((it) => (
          <Link
            key={`${it.target_type}-${it.id}`}
            to={it.link}
            className="flex-shrink-0 w-44 sm:w-48 snap-start bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow overflow-hidden"
            data-testid={`top-rated-${it.target_type}-${it.id}`}
          >
            <div className="relative aspect-[4/3] bg-slate-100">
              {it.image_url ? (
                <img
                  src={optimizeCloudinaryUrl(it.image_url, { width: 400 })}
                  alt={it.name}
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-slate-300">
                  {it.target_type === 'product' ? <Package className="w-10 h-10" /> : <StoreIcon className="w-10 h-10" />}
                </div>
              )}
              <span className={`absolute top-2 left-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-white shadow px-2 py-0.5 rounded-full ${
                it.target_type === 'product' ? 'bg-indigo-600' : 'bg-purple-600'
              }`}>
                {it.target_type === 'product' ? <Package className="w-3 h-3" /> : <StoreIcon className="w-3 h-3" />}
                {it.target_type === 'product' ? 'Produto' : 'Loja'}
              </span>
              <div className="absolute bottom-2 right-2">
                <RatingBadge value={it.avg} count={it.count} size="xs" />
              </div>
            </div>
            <div className="p-3">
              <p className="text-sm font-semibold text-slate-900 line-clamp-2 min-h-[2.5rem] leading-tight">{it.name}</p>
              {it.target_type === 'product' ? (
                <>
                  {it.store_name && (
                    <p className="text-[11px] text-slate-500 truncate mt-0.5">{it.store_name}</p>
                  )}
                  <div className="mt-1.5">
                    {it.promo_price ? (
                      <div className="flex flex-col">
                        <span className="text-[11px] text-slate-400 line-through">{formatPrice(it.price)}</span>
                        <span className="text-base font-black text-emerald-600">{formatPrice(it.promo_price)}</span>
                      </div>
                    ) : (
                      <span className="text-base font-black text-slate-900">{formatPrice(it.price)}</span>
                    )}
                  </div>
                </>
              ) : (
                <p className="text-[11px] text-slate-500 flex items-center gap-1 mt-1">
                  <MapPin className="w-3 h-3" />
                  {(it.city || '')}{it.city && it.state ? '/' : ''}{it.state || ''}
                </p>
              )}
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
};

export default TopRatedSection;
